/**
 * AgentMessageBody — text of an agent message plus its lesson cards (SUN-110c).
 *
 * Each `[[lesson:<id>]]` is asked from the server as the reader (GET /kiaan/lessons/<id>).
 * 404 / 403 / any failure → nothing is drawn and the token is hidden. A forbidden lesson in a
 * "not part of your role" reply gives the "Send to admin" button (POST /kiaan/requests).
 */
import { useEffect, useState } from 'react';
import { createLessonAccessRequest, getKiaanLesson, kiaanServerText, type KiaanLesson, type LessonLookup } from './kiaanApi';
import { findLesson } from '../../pages/tour/tourCatalog';
import { walkFor } from '../../pages/tour/tourWalks';
import { useTourStore } from '../../pages/tour/tourState';
import { KiaanMarkdown } from './KiaanMarkdown';
import { lessonIdsIn, saysNotMyRole, stripLessonTokens } from './lessonTokens';

export const REQUEST_SENT = 'Sent — an admin will look';

function openInApp(url: string) {
  if (window.location.pathname === url) return;
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function LessonCard({ lesson, onCollapse }: { lesson: KiaanLesson; onCollapse?: () => void }) {
  const startWalk = useTourStore((s) => s.startWalk);
  const catalogLesson = findLesson(lesson.id);
  const canShowMe = lesson.showMe.allowed && !!catalogLesson;
  return (
    <div className="kw-lesson" data-testid="lesson-card">
      <span className="kw-lesson-play" aria-hidden>▶</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <b>{lesson.id} · {lesson.title}</b>
        <div className="kw-small">{lesson.minutes} min · {lesson.platform}</div>
      </div>
      {lesson.videoUrl && (
        <a
          className="kw-btn"
          href={lesson.lessonUrl}
          onClick={(e) => { e.preventDefault(); openInApp(lesson.lessonUrl); onCollapse?.(); }}
        >
          Watch
        </a>
      )}
      {canShowMe && (
        <button
          type="button"
          className="kw-btn kw-light"
          onClick={() => { startWalk(walkFor(catalogLesson!)); onCollapse?.(); }}
        >
          Show me here
        </button>
      )}
    </div>
  );
}

export function AgentMessageBody({ text, onCollapse }: { text: string; onCollapse?: () => void }) {
  const ids = lessonIdsIn(text);
  const key = ids.join('|');
  const [found, setFound] = useState<Record<string, LessonLookup>>({});
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    for (const id of ids) {
      void getKiaanLesson(id).then((r) => { if (alive) setFound((f) => ({ ...f, [id]: r })); });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const shown = ids.map((id) => found[id]).filter((r): r is Extract<LessonLookup, { state: 'ok' }> => r?.state === 'ok');
  const forbiddenId = saysNotMyRole(text) ? ids.find((id) => found[id]?.state === 'forbidden') : undefined;

  async function sendToAdmin(lessonId: string) {
    setBusy(true);
    try {
      await createLessonAccessRequest(lessonId);
      setSent(lessonId);
      setErr(null);
    } catch (e) {
      setErr(kiaanServerText(e));
    } finally {
      setBusy(false);
    }
  }

  const body = stripLessonTokens(text);
  return (
    <>
      {body && <div className="kw-msg-text"><KiaanMarkdown text={body} /></div>}
      {shown.map((r) => <LessonCard key={r.lesson.id} lesson={r.lesson} onCollapse={onCollapse} />)}
      {forbiddenId && (
        <div style={{ marginTop: 6 }}>
          {sent === forbiddenId ? (
            <span className="kw-small" role="status">{REQUEST_SENT}</span>
          ) : (
            <button type="button" className="kw-btn kw-light" disabled={busy} onClick={() => void sendToAdmin(forbiddenId)}>
              Send to admin
            </button>
          )}
          {err && <div className="kw-small" role="status">{err}</div>}
        </div>
      )}
    </>
  );
}
